

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

interface LoadingContextType {
  isGlobalLoading: boolean;
  setGlobalLoading: (loading: boolean) => void;
  markAsReady: () => void;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export const LoadingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isGlobalLoading, setIsGlobalLoading] = useState(true);

  const markAsReady = useCallback(() => {
    // Add a small delay to ensure smooth transition
    setTimeout(() => {
      setIsGlobalLoading(false);
    }, 100);
  }, []);

  const setGlobalLoading = useCallback((loading: boolean) => {
    setIsGlobalLoading(loading);
  }, []);

  useEffect(() => {
    // Fallback: if loading takes too long (3 seconds), force show content
    const timeout = setTimeout(() => {
      if (isGlobalLoading) {
        setIsGlobalLoading(false);
      }
    }, 3000);

    return () => clearTimeout(timeout);
  }, [isGlobalLoading]);

  return (
    <LoadingContext.Provider value={{ isGlobalLoading, setGlobalLoading, markAsReady }}>
      {children}
    </LoadingContext.Provider>
  );
};

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};
